/* eslint-disable react/jsx-no-constructed-context-values */
import React, { createContext, useCallback, useContext, useMemo } from "react";

import { AllFeatureFlag, useEppoAllFeatureFlags } from "shared/hooks/useEppoAllFeatureFlags";
import { JsonFlagVariation, useEppoJsonFlags } from "shared/hooks/useEppoJsonFlags";

const EppoFeatureFlagsContext = createContext<{
  decisions: AllFeatureFlag;
  jsonFlags: JsonFlagVariation;
  isLoading: boolean;
  isLoadingJson: boolean;
  isJsonError: boolean;
  refetch: () => Promise<void>;
}>({
  decisions: null,
  jsonFlags: null,
  isLoading: false,
  isLoadingJson: false,
  isJsonError: false,
  refetch: async () => {},
});

type EppoFeatureFlagsProviderProps = {
  children: React.ReactNode;
};

export function EppoFeatureFlagsProvider({ children }: EppoFeatureFlagsProviderProps) {
  const { decisions, isLoading, refetch: refetchDecisions } = useEppoAllFeatureFlags();
  const {
    jsonFlags,
    isLoading: isLoadingJson,
    isError: isJsonError,
    refetch: refetchJson,
  } = useEppoJsonFlags();

  const refetch = useCallback(async () => {
    await Promise.all([refetchDecisions(), refetchJson()]);
  }, [refetchDecisions, refetchJson]);

  const value = useMemo(
    () => ({ decisions, jsonFlags, isLoading, isLoadingJson, isJsonError, refetch }),
    [decisions, jsonFlags, isLoading, isLoadingJson, isJsonError, refetch],
  );

  return (
    <EppoFeatureFlagsContext.Provider value={value}>{children}</EppoFeatureFlagsContext.Provider>
  );
}

export const useEppoFeatureFlagsProvider = () => useContext(EppoFeatureFlagsContext);
